import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { SupportRequest, SupportRequestType, SupportRequestStatus } from '@court-sync/shared';
import { supportService } from '../../services/support';
import { useCurrentAcademy } from '../../hooks/useDataStore';
import { useFormatDate } from '../../hooks/useFormatDate';
import { Button, Card, CardHeader, CardContent, Input, Badge, Modal, Field, FieldLabel } from '../../components/ui';
import {
  Loader2,
  Send,
  MessageSquare,
  Eye,
  Plus
} from 'lucide-react';

export default function SupportTab() {
  const { currentAcademy } = useCurrentAcademy();
  const { formatDateTime } = useFormatDate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Form state
  const [type, setType] = useState<SupportRequestType>('general');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Request history state
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<SupportRequest | null>(null);
  const [statusFilter, setStatusFilter] = useState<SupportRequestStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<SupportRequestType | 'all'>('all');

  // Auto-populate form from query params when URL has support params (e.g. from "contact support" link)
  useEffect(() => {
    const urlType = searchParams.get('type');
    const urlSubject = searchParams.get('subject');
    const urlMessage = searchParams.get('message');

    if (urlType || urlSubject || urlMessage) {
      setIsCreateModalOpen(true);
    }

    if (urlType) {
      const validTypes: SupportRequestType[] = ['technical', 'billing', 'feature_request', 'general'];
      if (validTypes.includes(urlType as SupportRequestType)) {
        setType(urlType as SupportRequestType);
      }
    }

    if (urlSubject) {
      setSubject(decodeURIComponent(urlSubject));
    }

    if (urlMessage) {
      setMessage(decodeURIComponent(urlMessage));
    }

    // Clear query params after populating so URL stays clean
    if (urlType || urlSubject || urlMessage) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('type');
      newParams.delete('subject');
      newParams.delete('message');
      newParams.delete('academyId');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Load support requests
  useEffect(() => {
    loadRequests();
  }, [statusFilter, typeFilter]);

  const loadRequests = async () => {
    setIsLoadingRequests(true);
    try {
      const filters: any = {};
      if (statusFilter !== 'all') {
        filters.status = statusFilter;
      }
      if (typeFilter !== 'all') {
        filters.type = typeFilter;
      }

      const response = await supportService.getSupportRequests(filters);
      if (response.success && response.data) {
        setRequests(response.data);
      }
    } catch (error: any) {
      console.error('Error loading support requests:', error);
      toast.error('Failed to load support requests');
    } finally {
      setIsLoadingRequests(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!subject.trim() || !message.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await supportService.createSupportRequest({
        type,
        subject: subject.trim(),
        message: message.trim(),
        academyId: currentAcademy?.id,
      });

      if (response.success) {
        toast.success('Support request submitted successfully!');
        // Reset form
        setSubject('');
        setMessage('');
        setType('general');
        setIsCreateModalOpen(false);
        // Reload requests
        loadRequests();
      } else {
        toast.error(response.error || 'Failed to submit support request');
      }
    } catch (error: any) {
      console.error('Error submitting support request:', error);
      toast.error(error.message || 'Failed to submit support request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadgeColor = (status: SupportRequestStatus) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'resolved':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'closed':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getTypeBadgeColor = (type: SupportRequestType) => {
    switch (type) {
      case 'technical':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'billing':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'feature_request':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200';
      case 'general':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const formatRequestDate = (date: Date | string) =>
    formatDateTime(date, undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <div className="space-y-6">
      {/* Request History Section - NOW AT TOP */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <MessageSquare className="h-5 w-5 mr-2" />
              Your Support Requests
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as SupportRequestStatus | 'all')}
                className="pl-3 pr-10 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as SupportRequestType | 'all')}
                className="pl-3 pr-10 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Types</option>
                <option value="general">General</option>
                <option value="technical">Technical</option>
                <option value="billing">Billing</option>
                <option value="feature_request">Feature Request</option>
              </select>
              <Button
                onClick={() => setIsCreateModalOpen(true)}
                size="sm"
                className="ml-0 sm:ml-2"
              >
                <Plus className="h-4 w-4 mr-1" />
                New Request
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoadingRequests ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
              <span className="ml-2 text-gray-600 dark:text-gray-400">Loading requests...</span>
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
              <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400 mb-4">No support requests found.</p>
              <Button variant="outline" onClick={() => setIsCreateModalOpen(true)}>
                Submit your first request
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((request) => (
                <div
                  key={request.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer group"
                  onClick={() => setSelectedRequest(request)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={getTypeBadgeColor(request.type)}>
                          {request.type.replace('_', ' ')}
                        </Badge>
                        <Badge className={getStatusBadgeColor(request.status)}>
                          {request.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                        {request.subject}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                        {request.message}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                        {formatRequestDate(request.createdAt)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedRequest(request);
                      }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Submit Form Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Submit a Support Request"
        maxWidth="2xl"
      >
        <div className="mb-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Need help? Submit a support request and we'll get back to you within 24 hours.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Request Type *
            </label>
            <select
              id="type"
              value={type}
              onChange={(e) => setType(e.target.value as SupportRequestType)}
              className="w-full pl-3 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              required
            >
              <option value="general">General Help</option>
              <option value="technical">Technical Issue</option>
              <option value="billing">Billing Question</option>
              <option value="feature_request">Feature Request</option>
            </select>
          </div>

          <Field>
            <FieldLabel htmlFor="subject">Subject *</FieldLabel>
            <Input id="subject" type="text" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Brief description of your request" required maxLength={255} />
          </Field>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Message *
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white resize-none"
              placeholder="Please provide details about your request..."
              required
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsCreateModalOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !subject.trim() || !message.trim()}
              className="min-w-[150px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Request Detail Modal - REFACTORED TO USE Modal COMPONENT */}
      <Modal
        isOpen={!!selectedRequest}
        onClose={() => setSelectedRequest(null)}
        title="Support Request Details"
        maxWidth="2xl"
      >
        {selectedRequest && (
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <Badge className={getTypeBadgeColor(selectedRequest.type)}>
                {selectedRequest.type.replace('_', ' ')}
              </Badge>
              <Badge className={getStatusBadgeColor(selectedRequest.status)}>
                {selectedRequest.status.replace('_', ' ')}
              </Badge>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Subject
              </label>
              <p className="text-lg font-medium text-gray-900 dark:text-white mt-1">{selectedRequest.subject}</p>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Message
              </label>
              <div className="mt-1 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-100 dark:border-gray-700">
                <p className="text-gray-900 dark:text-white whitespace-pre-wrap leading-relaxed">
                  {selectedRequest.message}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Submitted
                </label>
                <p className="text-gray-700 dark:text-gray-300 mt-1">
                  {formatRequestDate(selectedRequest.createdAt)}
                </p>
              </div>

              {selectedRequest.resolvedAt && (
                <div>
                  <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Resolved
                  </label>
                  <p className="text-gray-700 dark:text-gray-300 mt-1">
                    {formatRequestDate(selectedRequest.resolvedAt)}
                  </p>
                </div>
              )}
            </div>

            {selectedRequest.adminNotes && (
              <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Support Response
                </label>
                <div className="mt-2 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-100 dark:border-primary-900/30">
                  <p className="text-gray-900 dark:text-white whitespace-pre-wrap leading-relaxed">
                    {selectedRequest.adminNotes}
                  </p>
                </div>
              </div>
            )}

            <div className="pt-6 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase">
                Reference ID: {selectedRequest.id}
              </p>
              <Button variant="ghost" size="sm" onClick={() => setSelectedRequest(null)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
